import { useContext } from 'react';
import { Link } from 'react-router-dom';
import { Images } from '@urbanx/agx-ui-components';
import { Feature, useGetAgentProfile, useGetAgentsInAgency } from '@urbanx/agx.agencies.client';
import { AuthContext } from '@urbanx/agx.auth.client';
import { tokens } from '@urbanx/agx.ui-components2/tokens';
import { useAppSelector } from 'hooks/useAppSelector';
import {
  ActionMenu,
  AgxAvatar,
  AgxSpacer,
  AgxTypography,
  Icon_Flip2Outline,
  Icon_PersonOutline,
  type ActionMenuItem,
} from '@urbanx/agx.ui-components2';
import { useFeatureToggles } from 'hooks/useFeatureToggles';
import useLogout from 'hooks/useLogout';

type ProfileMenuAction = 'profile' | 'integrations' | 'logout';

const ProfileMenu = () => {
  const { userAccount } = useContext(AuthContext);
  const { isEnabled, isLoading } = useFeatureToggles();
  const { data: agentProfile } = useGetAgentProfile();
  const { data: agents } = useGetAgentsInAgency();
  const agencyName = useAppSelector((state) => state.agencies.agencyInfo.info.branding?.agencyName);
  const logout = useLogout();
  const agent = agents?.find(({ id }) => id === userAccount.agent);
  const firstName = agentProfile?.firstName ?? agent?.name?.firstName ?? userAccount.firstName;
  const lastName = agentProfile?.lastName ?? agent?.name?.lastName ?? userAccount.lastName;
  const fullName = [firstName, lastName].filter(Boolean).join(' ');
  const initials = `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase();

  if (isLoading || !isEnabled(Feature.AgentProfileSettings)) return null;

  const actions: ActionMenuItem<ProfileMenuAction>[] = [
    {
      action: 'profile',
      to: '/settings/profile',
      children: (
        <AgxSpacer direction="horizontal" gap="spacing-sm" contentAlignment="center">
          <AgxAvatar
            className="profileMenu__itemIcon"
            type="icon-light"
            size="xs"
            icon={<Icon_PersonOutline />}
          />
          <AgxTypography
            id="profile-menu-profile-label"
            type="body_ui"
            size="small"
            color="inherit"
          >
            Profile
          </AgxTypography>
        </AgxSpacer>
      ),
    },
    {
      action: 'integrations',
      to: '/settings/integrations',
      children: (
        <AgxSpacer direction="horizontal" gap="spacing-sm" contentAlignment="center">
          <AgxAvatar
            className="profileMenu__itemIcon"
            type="icon-light"
            size="xs"
            icon={<Icon_Flip2Outline />}
          />
          <AgxTypography
            id="profile-menu-integrations-label"
            type="body_ui"
            size="small"
            color="inherit"
          >
            Integrations
          </AgxTypography>
        </AgxSpacer>
      ),
    },
    {
      action: 'logout',
      onClick: logout,
      children: (
        <AgxSpacer direction="horizontal" gap="spacing-sm" contentAlignment="center">
          <AgxAvatar
            className="profileMenu__itemIcon"
            type="icon-light"
            size="xs"
            icon={<Images.Logout />}
          />
          <AgxTypography id="profile-menu-logout-label" type="body_ui" size="small" color="inherit">
            Logout
          </AgxTypography>
        </AgxSpacer>
      ),
    },
  ];

  return (
    <ActionMenu
      id="profile-menu"
      actions={actions}
      dataTestId="profile-menu"
      className="profileMenu"
      menuClassName="profileMenu__menu"
      triggerAriaLabel="Open profile menu"
      header={
        <AgxSpacer gap="spacing-xs" padding="spacing-sm" hideOverflow>
          <AgxTypography id="profile-menu-name" type="body_ui" size="small" overflowEllipsis>
            {fullName}
          </AgxTypography>
          {(agentProfile?.email || agencyName) && (
            <AgxSpacer gap="spacing-xxs" hideOverflow>
              {agentProfile?.email && (
                <AgxTypography
                  id="profile-menu-email"
                  type="caption"
                  size="medium"
                  color={tokens.text.tertiary}
                  overflowEllipsis
                >
                  {agentProfile.email}
                </AgxTypography>
              )}
              {agencyName && (
                <AgxTypography
                  id="profile-menu-agency"
                  type="caption"
                  size="medium"
                  color={tokens.text.tertiary}
                  overflowEllipsis
                >
                  {agencyName}
                </AgxTypography>
              )}
            </AgxSpacer>
          )}
        </AgxSpacer>
      }
      trigger={
        agent?.photoUrl ? (
          <AgxAvatar
            className="profileMenu__avatar"
            type="image"
            size="md"
            imageUri={agent.photoUrl}
          />
        ) : (
          <AgxAvatar className="profileMenu__avatar" type="text-dark" size="md" text={initials} />
        )
      }
      renderLink={({ to, children, ...props }) => (
        <Link to={to} {...props}>
          {children}
        </Link>
      )}
    />
  );
};

export default ProfileMenu;
